import { Icon } from '@buggy/shared'
import { Tooltip } from 'antd'
import { TooltipProps } from 'antd/lib/tooltip'
import { isEmpty } from 'lodash-es'
import { FunctionComponent, ReactNode } from 'react'
import { useCss } from 'react-use'
import styled, { useTheme } from 'styled-components'
import { useUnlockKolDetailPermission } from '@/hooks/use-permission'
import { useIntl } from '@/i18n/hooks/use-intl'
import {
  type AvailableUnlockData,
  type AvailableUnlockDataName,
  StatisticStatusValue,
} from '@/types/schema/kol/unlock'
import { convertPlatformShortcode } from '@/utils/convert-platform'
import { exhaustiveCheck } from '@/utils/exhaustive-check'
import { isAllPlatformStatisticStatusValue } from '@/utils/kol/is-all-platform-statistic-status-value'

interface UnlockSnapshotTooltipProps {
  availableUnlockData?: AvailableUnlockData[]
  placement?: TooltipProps['placement']
  children?: ReactNode
  style?: 'default' | 'compact'
}

const UnlockSnapshotTooltip: FunctionComponent<UnlockSnapshotTooltipProps> = ({
  availableUnlockData,
  placement = 'bottomRight',
  children,
  style = 'default',
}) => {
  const { formatMessage } = useIntl()
  const theme = useTheme()
  const tooltipOverlayStyle = useCss({
    '&': {
      '--ant-color-bg-spotlight': theme.colors.base.grey.grey9,
      maxWidth: 'none',
      '& .ant-tooltip-inner': {
        padding: 0,
      },
    },
  })

  const unlockPermission = useUnlockKolDetailPermission()
  const displayedUnlockData = availableUnlockData?.filter((statisticStatus) =>
    isAllPlatformStatisticStatusValue(statisticStatus.status)
      ? statisticStatus.status !== StatisticStatusValue.NotCalculated
      : statisticStatus.status.some(
          (item) => item.status !== StatisticStatusValue.NotCalculated,
        ),
  )
  if (!unlockPermission) {
    return (
      <Tooltip
        getTooltipContainer={(node): HTMLElement =>
          node.parentElement ?? document.body
        }
        title={formatMessage({
          id: 'workspace:insufficient_permission_tooltip',
        })}
      >
        {children}
      </Tooltip>
    )
  }

  if (!displayedUnlockData?.length) {
    return children
  }

  const getStatusTag = (status: StatisticStatusValue): ReactNode => {
    switch (status) {
      case StatisticStatusValue.NotCalculated:
        return null
      case StatisticStatusValue.Calculating:
        return (
          <Tooltip
            title={formatMessage({ id: 'general:statistic_calculating' })}
          >
            <AlertCircleIconWrapper>
              <Icon fontSize={16} name='alert-circle' />
            </AlertCircleIconWrapper>
          </Tooltip>
        )
      case StatisticStatusValue.InsufficientData:
        return (
          <Tooltip
            title={formatMessage({ id: 'general:statistic_insufficient' })}
          >
            <AlertCircleIconWrapper>
              <Icon fontSize={16} name='alert-circle' />
            </AlertCircleIconWrapper>
          </Tooltip>
        )
      case StatisticStatusValue.ZeroData:
      case StatisticStatusValue.SufficientData:
        return (
          <Tooltip
            title={formatMessage({ id: 'general:statistic_sufficient' })}
          >
            <CheckIconWrapper>
              <Icon fontSize={14} name='circle-check-filled' />
            </CheckIconWrapper>
          </Tooltip>
        )
      default:
        return exhaustiveCheck(status)
    }
  }

  const getLocalizedStatisticName = (key: AvailableUnlockDataName): string => {
    switch (key) {
      case 'overview':
        return formatMessage({ id: 'kol:side_nav_overview' })
      case 'k_score':
        return formatMessage({
          id: 'project:collection_export_basic_info_influencer_score',
        })
      case 'customized_tag':
        return formatMessage({ id: 'kol:side_nav_contents_tag' })
      case 'audience':
        return formatMessage({ id: 'kol:side_nav_audience' })
      case 'mention_brand_kol':
        return formatMessage({ id: 'kol:mentioned_brands_and_influencers' })
      case 'similar_kol':
        return formatMessage({ id: 'kol:similar_kols' })
      case 'fans_growth':
        return formatMessage({ id: 'kol:follower_analysis_title' })
      case 'engagement':
        return formatMessage({ id: 'kol:side_nav_engagement' })
      case 'view_analysis':
        return formatMessage({ id: 'kol:side_nav_video' })
      case 'post_frequency':
        return formatMessage({ id: 'kol:side_nav_contents_fre' })
      case 'hashtag_analysis':
        return formatMessage({ id: 'kol:hashtag_analysis' })
      case 'sponsored_post_analysis':
        return formatMessage({ id: 'kol:sponsored_posts_analysis_title' })
      default:
        return exhaustiveCheck(key)
    }
  }

  const isSufficientDataStatus = (value: StatisticStatusValue): boolean => {
    return [
      StatisticStatusValue.ZeroData,
      StatisticStatusValue.SufficientData,
    ].includes(value)
  }

  if (style === 'compact') {
    const insufficientData = displayedUnlockData.filter((statisticStatus) =>
      isAllPlatformStatisticStatusValue(statisticStatus.status)
        ? !isSufficientDataStatus(statisticStatus.status)
        : !statisticStatus.status.some((item) =>
            isSufficientDataStatus(item.status),
          ),
    )

    if (insufficientData.length === 0) {
      return children
    }

    const title = [
      formatMessage({
        id: 'kol:description_insufficient_data_tooltip',
      }),
      ...insufficientData.map((statisticStatus) => {
        return `・${getLocalizedStatisticName(statisticStatus.name)}`
      }),
    ].join('\n')

    return <Tooltip title={title}>{children}</Tooltip>
  }

  return (
    <Tooltip
      overlayClassName={tooltipOverlayStyle}
      placement={placement}
      title={
        <Wrapper>
          <Header>
            {formatMessage({ id: 'kol:description_after_unlock' })}
          </Header>
          <Content>
            <Description>
              {formatMessage({
                id: 'kol:description_after_unlock_description',
              })}
            </Description>
            {!isEmpty(availableUnlockData) && (
              <AvailableUnlockInfoList>
                {displayedUnlockData.map((statisticStatus) => {
                  const [status, platformTitles] = ((): [
                    StatisticStatusValue,
                    string[],
                  ] => {
                    if (
                      isAllPlatformStatisticStatusValue(statisticStatus.status)
                    ) {
                      return [statisticStatus.status, []]
                    }

                    const sufficientPlatforms = statisticStatus.status.filter(
                      (item) => isSufficientDataStatus(item.status),
                    )

                    return sufficientPlatforms.length
                      ? [
                          StatisticStatusValue.SufficientData,
                          sufficientPlatforms.map(
                            (item) =>
                              convertPlatformShortcode(item.platform).title,
                          ),
                        ]
                      : [StatisticStatusValue.InsufficientData, []]
                  })()

                  return (
                    <li key={statisticStatus.name}>
                      {getStatusTag(status)}
                      {getLocalizedStatisticName(statisticStatus.name)}
                      {platformTitles.length > 0 && (
                        <>({platformTitles.join(', ')})</>
                      )}
                    </li>
                  )
                })}
              </AvailableUnlockInfoList>
            )}
          </Content>
        </Wrapper>
      }
    >
      {children}
    </Tooltip>
  )
}

const Header = styled.div`
  border-bottom: solid 1px ${({ theme }): string => theme.colors.border.border};
  padding: 6px 16px;
  font-size: 15px;
  font-weight: 700;
  line-height: 22px;
`

const Description = styled.div``

const CheckIconWrapper = styled.div`
  align-self: flex-start;
  line-height: 1;
  padding: 2px 1px 0;
  color: ${({ theme }): string => theme.colors.text.white};

  svg {
    fill: ${({ theme }): string => theme.colors.level.level2};
  }
`

const AlertCircleIconWrapper = styled.div`
  line-height: 1;
  color: ${({ theme }): string => theme.colors.text.white};

  svg {
    fill: ${({ theme }): string => theme.colors.text.tooltip};
  }
`

const AvailableUnlockInfoList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 4px;
  line-height: 1.4;
  padding-inline-start: 0;

  li {
    display: flex;
    align-items: center;
    gap: 4px;
    color: ${({ theme }): string => theme.colors.text.secondary};
    font-size: 14px;
    font-weight: 400;
    line-height: 140%;
  }
`

const Content = styled.div`
  padding: 12px 16px;

  ${Description} {
    margin: 0 0 12px 0;
  }
`

const Wrapper = styled.div`
  border-radius: ${({ theme }): number => theme.token.borderRadius}px;
  width: 320px;
  display: flex;
  flex-direction: column;
  font-size: 14px;
  padding: 0;
  background: ${({ theme }): string => theme.colors.background.white};
  color: ${({ theme }): string => theme.colors.text.secondary};
`

export default UnlockSnapshotTooltip
